import { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Checkbox,
  Paper,
  TextField,
  CircularProgress,
} from "@mui/material";
import { Box } from "@mui/system";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import SnoozeOutlinedIcon from "@mui/icons-material/SnoozeOutlined";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import { DataAccuracyEndPointLogic } from "../../../../../logic/AssetManagement/Health/Data Accuracy/dataAccuracyEndPointLogic";
import {
  selectIsLoading,
  selectUserToken,
  selectUserWebsitePermissions,
  setDisplayErrorSnackBar,
  setIsLoading,
} from "../../../../../features/userSlice";
import store from "../../../../../store";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";
import {
  setIsSuccess,
  setReloadKey,
} from "../../../../../features/dataAccuracySlice";
import { getSiteInfo } from "../../../../../utils/helpers";
import DataStatusAlert from "../UI_HelpersOf_DataAccuracy/DataStatusAlert_Helpers";

const SnoozeDataAccuracy = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [reason, setReason] = useState<string>("");
  const { isSuccess, selectedRowsData = [] } = useSelector(
    (state: any) => state.dataAccuracy
  );
  const allWebsites = useSelector(selectUserWebsitePermissions);
  const [selected, setSelected] = useState<any[]>([]);

  const userToken = useSelector(selectUserToken);
  const loading = useSelector(selectIsLoading);
  const dispatch = useDispatch();

  // Keep local `selected` in sync whenever dialog opens or selectedRowsData changes
  useEffect(() => {
    if (open) {
      setSelected(selectedRowsData || []);
    }
  }, [selectedRowsData, open]);

  const snoozeBody = {
    tasks: selected.map((task) => {
      const taskObj: any = {
        current_status: "Snooze",
        snooze_end: selectedDate,
        shared_uid: task?.shared_uid,
      };

      if (reason?.trim()) {
        taskObj.reason = reason.trim();
      }

      return taskObj;
    }),
  };

  const isAllSelected =
    selected.length > 0 && selected.length === selectedRowsData.length;

  const handleSelectRow = (row: any) => {
    setSelected((prev) =>
      prev.some((r) => r === row)
        ? prev.filter((r) => r !== row)
        : [...prev, row]
    );
  };

  const handleSelectAll = () => {
    setSelected(isAllSelected ? [] : [...selectedRowsData]);
  };

  const clearLocalState = () => {
    setSelected([]);
    setSelectedDate(null);
    setReason("");
  };

  const callAPIs = async () => {
    try {
      if (selected.length > 0 && selectedDate) {
        store.dispatch(setIsLoading(true));

        await DataAccuracyEndPointLogic.updateDataAccuracyTask(
          userToken,
          snoozeBody,
          dispatch
        );

        // On success
        clearLocalState();
        store.dispatch(setReloadKey(true));
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Snooze Success",
            type: "success",
          })
        );

        onClose();
      } else {
        // optionally show an error if validation fails
        store.dispatch(
          setDisplayErrorSnackBar({
            message:
              "Please select tasks, a valid snooze date and provide a reason.",
            type: "error",
          })
        );
      }
    } catch (error) {
      console.error("Snooze API error:", error);
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "Snooze failed. Please try again.",
          type: "error",
        })
      );
    } finally {
      store.dispatch(setIsLoading(false));
    }
  };

  useEffect(() => {
    if (isSuccess) {
      store.dispatch(setIsSuccess(false));
    }
    // eslint-disable-next-line
  }, [isSuccess]);

  return (
    <Dialog open={open} onClose={() => {}} maxWidth="md" fullWidth>
      {loading && <SimpleBackdrop />}
      <DialogTitle>Snooze Tasks</DialogTitle>
      <DialogContent>
        <Typography gutterBottom sx={{ paddingBottom: "8px" }}>
          Selected Tasks
        </Typography>
        <Paper sx={{ width: "100%", overflowY: "auto", maxHeight: "225px" }}>
          <Table size="small" aria-label="a dense table">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ padding: "4px" }}>
                  <Checkbox
                    size="small"
                    color="secondary"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ padding: "4px" }}>Task Name</TableCell>
                <TableCell sx={{ padding: "4px" }}>Asset</TableCell>
                <TableCell sx={{ padding: "4px" }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedRowsData.map((row: any, index: number) => (
                <TableRow
                  key={index}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell padding="checkbox" sx={{ padding: "4px" }}>
                    <Checkbox
                      size="small"
                      color="secondary"
                      checked={selected.some((r) => r === row)}
                      onChange={() => handleSelectRow(row)}
                    />
                  </TableCell>
                  <TableCell sx={{ padding: "4px" }}>
                    {row?.task_structure__title}
                  </TableCell>
                  <TableCell sx={{ padding: "4px" }}>
                    {getSiteInfo(allWebsites, row?.website_shared_uid)}
                  </TableCell>

                  <TableCell align="left" sx={{ padding: "4px" }}>
                    <DataStatusAlert status={row.current_status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        <Box sx={{ paddingTop: 2 }}>
          {/* Use flex with equal-flex children so both fields align nicely */}
          <Box display="flex" gap={2} alignItems="center">
            <Box sx={{ flex: 1 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DatePicker"]}>
                  <DatePicker
                    className="brandDataDatePicker"
                    slotProps={{
                      textField: {
                        size: "small",
                        color: "secondary",
                        required: true,
                        onKeyDown: (e: any) => e.preventDefault(),
                        sx: { width: "100%" },
                      },
                    }}
                    minDate={dayjs().startOf("day")}
                    label="Snooze Until"
                    onChange={(date: any) => {
                      if (date) {
                        setSelectedDate(
                          dayjs(date).format("YYYY-MM-DD HH:mm:ss")
                        );
                      } else {
                        setSelectedDate(null);
                      }
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
            </Box>

            <Box sx={{ flex: 1 }}>
              <TextField
                label="Reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                size="small"
                color="secondary"
                fullWidth
              />
            </Box>
          </Box>
        </Box>

        <Typography
          gutterBottom
          sx={{ fontSize: "12px", paddingBottom: "2px", color: "#666666" }}
        >
          All fields are mandatory
        </Typography>
      </DialogContent>
      <DialogActions sx={{ marginBottom: "16px", paddingRight: "40px" }}>
        <Button
          onClick={() => {
            // clear selection when user cancels
            if (!loading) {
              clearLocalState();
              onClose();
            }
          }}
          color="secondary"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          sx={{ whiteSpace: "nowrap", width: "140px" }}
          disabled={loading || !selectedDate || selected.length === 0}
          onClick={callAPIs}
          data-tracker="172"
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            <>
              SNOOZE
              <SnoozeOutlinedIcon
                sx={{ color: "#fff", marginLeft: "5px" }}
                fontSize="small"
              />
            </>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SnoozeDataAccuracy;
